import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Category, ShopSection, ShopSettings } from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { CarouselResponsiveOptions } from 'primeng/carousel';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-brands-form',
  templateUrl: './brands-form.component.html',
  styleUrl: './brands-form.component.scss'
})
export class BrandsFormComponent implements OnInit {
  form: FormGroup;
  settings: Partial<ShopSettings> & { sections?: ShopSection[] };
  brands: Category[] = [];
  responsiveOptionsSmall: CarouselResponsiveOptions[] = [
    {
      breakpoint: '1199px',
      numVisible: 4,
      numScroll: 4
    },
    {
      breakpoint: '991px',
      numVisible: 3,
      numScroll: 3
    },
    {
      breakpoint: '767px',
      numVisible: 2,
      numScroll: 2
    }
  ];
  private messageService = inject(MessageService);
  private shopSettingsService = inject(ShopSettingsService);
  protected categoryService = inject(ShopCategoryService);

  constructor(
    private fb: FormBuilder,
    readonly router: Router,
    readonly activatedRoute: ActivatedRoute
  ) {
    this.form = this.fb.group({
      id: [''],
      type: ['BRANDS'],
      fixed: [false],
      editable: [true],
      selectedBrand: [null],
      brands: this.fb.array([])
    });
  }

  async ngOnInit(): Promise<void> {
    this.settings = await this.shopSettingsService.getById('GLAM_NEW_HOME');
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      // Edit mode: Load existing section data
      const section = this.settings.sections?.find((s) => s.id === id);
      // Populate form with existing data
      if (section) {
        this.form.patchValue({ id, ...section });
        if (section.brands) {
          section.brands.forEach((brand) => {
            this.brandsFormArray.push(this.fb.group({ ...brand }));
          });
        }
      }
    }

    console.log('this.settings', this.settings);
  }

  get brandsFormArray(): FormArray {
    return this.form.get('brands') as FormArray;
  }

  get previewBrands() {
    return this.brandsFormArray.value.map((brand: any) => ({
      name: brand.brandName,
      images: [brand.image]
    }));
  }

  addBrand(event: any): void {
    const brand = event.value as Category;

    if (brand) {
      const images: {
        image: string;
        brandBanner: string;
        brandBannerMobile: string;
      } = brand.glamImages
        ? brand.glamImages
        : brand.mensImages
          ? brand.mensImages
          : null;

      const image = brand.brandLogo
        ? brand.brandLogo
        : images
          ? images.image
          : null;
      const brandGroup = this.fb.group({
        slug: [brand.slug, Validators.required],
        image: [image, Validators.required],
        brandName: [brand.name, Validators.required]
      });
      this.brandsFormArray.push(brandGroup);
      this.form.get('selectedBrand')?.setValue(null);
    }
  }

  removeBrand(index: number): void {
    this.brandsFormArray.removeAt(index);
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(
      this.brandsFormArray.controls,
      event.previousIndex,
      event.currentIndex
    );
  }

  async searchBrands(event: AutoCompleteCompleteEvent): Promise<void> {
    const { content } = await this.categoryService.getCategoriesList({
      filters: [
        {
          condition: 'contains',
          field: 'name',
          value: event.query,
          fieldType: 'text'
        },
        {
          condition: 'equals',
          field: 'brandCategory',
          value: true,
          fieldType: 'boolean'
        }
      ],
      page: 0,
      pageSize: 10
    });

    this.brands = content;
  }

  async onSubmit(): Promise<void> {
    const totalBrands = this.brandsFormArray.length;

    if (totalBrands < 12) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'É necessário adicionar pelo menos 12 marcas'
      });
      return;
    }

    if (this.form.valid) {
      LoaderService.showLoader();
      const newSection = this.getBrandsData();
      const sections = this.settings.sections || [];
      const existingIndex = sections.findIndex(
        (s) => s.id === this.activatedRoute.snapshot.paramMap.get('id')
      );

      if (existingIndex >= 0) {
        sections[existingIndex] = newSection;
      } else {
        sections.push(newSection);
      }

      await this.shopSettingsService.update({
        id: this.settings.id,
        sections: sections
      });
    }
    LoaderService.showLoader(false);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Marcas salvas com sucesso'
    });

    this.router.navigate(['/configuracoes/glam/new-home']);
  }

  private getBrandsData() {
    const formValue = this.form.value;
    return {
      id: formValue.id || crypto.randomUUID(),
      type: formValue.type,
      fixed: formValue.fixed,
      editable: formValue.editable,
      brands: formValue.brands
    };
  }
}
